import { useEffect, useRef, useState } from 'react'
import { api, api_error, type payment, type portal_overview } from '../api'
import {
  format_amount,
  format_bytes,
  format_date,
  format_timestamp,
  is_outgoing,
  status_label,
} from '../format'

/**
 * Die einzige Seite, die ein Benutzer sieht. Standardfilter ist "noch zu
 * erledigen": wer aus einer Erinnerungsmail kommt, soll nicht in dreihundert
 * Zeilen suchen, welche vier gemeint sind.
 */
export function PortalPage() {
  const [overview, set_overview] = useState<portal_overview | null>(null)
  const [locked, set_locked] = useState(false)
  const [error, set_error] = useState<string | null>(null)
  const [only_todo, set_only_todo] = useState(true)
  const [busy_payment_id, set_busy_payment_id] = useState<number | null>(null)

  async function load() {
    try {
      set_overview(await api.portal_overview())
      set_locked(false)
    } catch (caught) {
      if (caught instanceof api_error && caught.status === 401) set_locked(true)
      else set_error(caught instanceof Error ? caught.message : String(caught))
    }
  }

  useEffect(() => {
    void load()
  }, [])

  if (locked) {
    return (
      <div className="page center">
        <div className="card">
          <h1>Kein gültiger Zugang</h1>
          <p className="muted">
            Dieser Anmeldelink ist abgelaufen. Sobald wieder Belege von Ihnen benötigt werden,
            erhalten Sie automatisch eine E-Mail mit einem neuen Link.
          </p>
        </div>
      </div>
    )
  }

  if (!overview) {
    return (
      <div className="page">
        <p className="muted">Wird geladen …</p>
      </div>
    )
  }

  const todo = (item: payment) =>
    item.receipt_required && (item.review_status === 'open' || item.review_status === 'rejected')

  const open_count = overview.payments.filter(todo).length
  const visible = only_todo ? overview.payments.filter(todo) : overview.payments

  async function with_busy(payment_id: number, action: () => Promise<unknown>) {
    set_busy_payment_id(payment_id)
    set_error(null)
    try {
      await action()
      await load()
    } catch (caught) {
      set_error(caught instanceof Error ? caught.message : String(caught))
    } finally {
      set_busy_payment_id(null)
    }
  }

  return (
    <div className="page">
      <h1>Ihre Zahlungen</h1>
      <p className="muted">
        {overview.name} · {overview.email}
      </p>

      {error && <div className="notice notice--error">{error}</div>}

      <div className="card">
        <div className="row">
          <strong>
            {open_count === 0
              ? 'Es fehlen aktuell keine Belege.'
              : open_count === 1
                ? 'Für 1 Zahlung fehlt noch ein Beleg.'
                : `Für ${open_count} Zahlungen fehlen noch Belege.`}
          </strong>
          <span style={{ flex: 1 }} />
          <label className="row small" style={{ marginBottom: 0 }}>
            <input
              type="checkbox"
              checked={only_todo}
              onChange={(event) => set_only_todo(event.target.checked)}
              style={{ width: 'auto' }}
            />
            Nur noch zu erledigende anzeigen
          </label>
        </div>
      </div>

      <div className="card">
        <table>
          <thead>
            <tr>
              <th>Datum</th>
              <th>Empfänger / Zahler</th>
              <th>Verwendungszweck</th>
              <th className="amount">Betrag</th>
              <th>Beleg</th>
            </tr>
          </thead>
          <tbody>
            {visible.map((item) => (
              <PaymentRow
                key={item.id}
                payment={item}
                max_bytes={overview.max_upload_bytes}
                max_receipts={overview.max_receipts_per_payment}
                busy={busy_payment_id === item.id}
                on_upload={(file) => with_busy(item.id, () => api.upload_receipt(item.id, file))}
                on_delete={(receipt_id) =>
                  with_busy(item.id, () => api.delete_receipt(receipt_id))
                }
              />
            ))}
            {visible.length === 0 && (
              <tr>
                <td colSpan={5} className="muted">
                  Nichts zu erledigen.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>
    </div>
  )
}

function PaymentRow(props: {
  payment: payment
  max_bytes: number
  max_receipts: number
  busy: boolean
  on_upload: (file: File) => void
  on_delete: (receipt_id: number) => void
}) {
  const { payment: item } = props
  const file_input = useRef<HTMLInputElement>(null)

  const frozen = item.review_status === 'accepted'
  const can_upload =
    item.receipt_required && !frozen && item.receipts.length < props.max_receipts

  return (
    <tr>
      <td style={{ whiteSpace: 'nowrap' }}>{format_date(item.booking_date)}</td>
      <td>
        {item.counterparty_name}
        {item.iban && <div className="small muted">{item.iban}</div>}
      </td>
      <td>{item.purpose}</td>
      <td className={`amount ${is_outgoing(item.amount) ? 'outgoing' : 'incoming'}`}>
        {format_amount(item.amount)}
      </td>
      <td>
        {!item.receipt_required ? (
          <span className="status status--none">Kein Beleg erforderlich</span>
        ) : (
          <div className="stack">
            <span className={`status status--${item.review_status}`}>
              {status_label[item.review_status]}
            </span>

            {item.review_status === 'rejected' && item.rejection_notes[0] && (
              <div className="notice notice--error small" style={{ margin: '4px 0' }}>
                <strong>Abgelehnt am {format_timestamp(item.rejection_notes[0].created_at)}:</strong>{' '}
                {item.rejection_notes[0].note}
              </div>
            )}

            {item.receipts.map((receipt) => (
              <div key={receipt.id} className="row small">
                <a href={`/api/portal/receipts/${receipt.id}/file`} target="_blank" rel="noreferrer">
                  {receipt.file_name}
                </a>
                <span className="muted">{format_bytes(receipt.byte_size)}</span>
                {!frozen && (
                  <button
                    className="danger"
                    disabled={props.busy}
                    onClick={() => props.on_delete(receipt.id)}
                  >
                    Entfernen
                  </button>
                )}
              </div>
            ))}

            {frozen && item.receipts.length > 0 && (
              <span className="small muted">Angenommen — nicht mehr änderbar.</span>
            )}

            {can_upload && (
              <div>
                <input
                  ref={file_input}
                  type="file"
                  accept="application/pdf"
                  hidden
                  onChange={(event) => {
                    const file = event.target.files?.[0]
                    if (file) props.on_upload(file)
                    event.target.value = ''
                  }}
                />
                <button
                  className="primary"
                  disabled={props.busy}
                  onClick={() => file_input.current?.click()}
                >
                  {props.busy ? 'Wird hochgeladen …' : 'PDF hochladen'}
                </button>
                <div className="small muted">
                  Nur PDF, höchstens {Math.round(props.max_bytes / 1024 / 1024)} MB.
                </div>
              </div>
            )}
          </div>
        )}
      </td>
    </tr>
  )
}
